import { Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxDateRangeInputSelect, {
    CustomDateInputStyles,
} from "components/UI/Calendar/DateRange/AcxDateRangeInputSelect";
import { observer } from "mobx-react";
import React from "react";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import { useStore } from "utils/useStore";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import AcxHierarchySelector from "components/UI/AcxHierarchySelector/AcxHierarchySelector";
import ClassifierBuilderV2ReclassifyDialogStore, {
    ReclassifyClassifierOption,
} from "../../Stores/ClassifierBuilderV2ReclassifyDialogStore";
import AcxToast from "components/UI/AcxToast";
import { formatUsd } from "components/Admin/Organizations/OrganizationDetail/KboFlowDebug/kboModelPricing";

const dateRangeStyles: CustomDateInputStyles = {
    containerWrapper: {
        flexDirection: "column",
        justifyContent: "flex-start",
        width: "100%",
    },
    dateInputWrapper: { paddingBottom: "1rem", minWidth: "100%" },
    dateInnerStyles: {
        flexDirection: "row-reverse",
        paddingRight: "1rem",
    },
    dateRefWrapper: { minWidth: "100%" },
};

interface Props {
    defaultValue?: ReclassifyClassifierOption[];
    showUnpublishedText?: boolean;
    classifierOptions: ReclassifyClassifierOption[];
}

const ClassifierBuilderV2ReclassifyDialog = observer(
    ({ defaultValue, showUnpublishedText, classifierOptions }: Props) => {
        const store = useStore(ClassifierBuilderV2ReclassifyDialogStore);

        function reclassify() {
            store.reclassify();
        }

        function closeDialog() {
            store.dialog.close();
        }

        function closeConfirmDialog() {
            store.confirmDialog.close();
        }

        function clearProgress() {
            store.completedCount = 0;
            store.totalCount = 0;
        }

        const isLlm = store.classifierType === "LLM";
        const reclassifyLimitExceeded = store.reclassifyLimitExceeded;
        const costEstimate = store.llmReclassifyCostEstimate;

        const indicator = store.loadingAffected ? (
            <AcxLoadingIndicator
                color="secondary"
                alternate="PuffLoader"
                size={32}
            />
        ) : store.succeeded >= 0 ? (
            "Reclassification process started."
        ) : (
            <Grid
                container
                item
                justifyContent="flex-start"
                alignItems="center"
                direction="row"
                wrap="nowrap"
                sx={{ display: "flex" }}
            >
                <Typography sx={{ fontWeight: "bold" }}>
                    {store.numAffectedInteractions !== undefined &&
                        store.numAffectedInteractions !== null &&
                        (isLlm
                            ? `Records to be reclassified (up to):`
                            : `Total Records to be reclassified:`)}
                </Typography>
                <Typography sx={{ marginLeft: ".5rem" }}>
                    {store.numAffectedInteractions !== undefined &&
                        store.numAffectedInteractions !== null &&
                        `  ${store.numAffectedInteractions.toLocaleString()}`}
                </Typography>
            </Grid>
        );

        const referenceOptions = [
            DateReferenceOption.ArrivalDate,
            DateReferenceOption.InteractionDate,
        ];

        const reclassifyLimitExceededError = (
            <Grid
                item
                container
                xs={12}
                justifyContent="center"
                direction="column"
                sx={{ maxWidth: "455px" }}
            >
                <Typography
                    sx={{ fontWeight: "bold" }}
                    align="center"
                    color="error"
                >
                    Reclassify Volume Limit Exceeded
                </Typography>
                <Typography align="center" color="error">
                    {isLlm ? (
                        <>
                            You have exceeded the maximum reclassify volume of{" "}
                            {store.reclassifyLimit.toLocaleString()} records.
                            Try decreasing the date range, or selecting fewer
                            models.
                        </>
                    ) : (
                        <>
                            You have exceeded the maximum reclassify volume. Try
                            decreasing the date range, or selecting fewer
                            classifiers.
                        </>
                    )}
                </Typography>
            </Grid>
        );

        const reclassifyDialogContent = (
            <Grid
                container
                item
                spacing={3}
                alignItems="center"
                direction="column"
                justifyContent="center"
            >
                {showUnpublishedText && (
                    <Grid item container xs={12} alignItems="center">
                        <AcxToast
                            title="You have unpublished classifiers selected"
                            message="Unpublished classifiers can't be
                                reclassified"
                            severity="error"
                        />
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    container
                    spacing={1}
                    alignItems="center"
                    sx={{ marginTop: showUnpublishedText ? 0 : 1 }}
                >
                    <AcxDateRangeInputSelect
                        label="Select Date Range"
                        datePickerStore={store.datePickerStore}
                        dateRangeStyles={dateRangeStyles}
                        dateReferenceOptions={referenceOptions}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    container
                    alignItems="center"
                    sx={{ paddingBottom: "8px", maxWidth: "480px" }}
                >
                    <AcxSelectMulti
                        inputLabel={
                            isLlm ? "Select Model(s)" : "Select Classifier(s)"
                        }
                        fullWidth
                        options={classifierOptions}
                        onChange={store.setSelectedClassifiers}
                        id="user-classifiers"
                        valueField="id"
                        labelField="name"
                        placeholder={
                            isLlm ? "Select model(s)" : "Select classifier(s)"
                        }
                        defaultValue={
                            defaultValue && defaultValue.length > 0
                                ? defaultValue
                                : store.selectedPublishedClassifiers
                        }
                        containerHeight="auto"
                        maxContainerHeight="96px"
                        alignControls="flex-start"
                        isClearable={true}
                    />
                </Grid>
                <Grid item xs={12} container alignItems="center">
                    <AcxHierarchySelector
                        displayType="inputDialog"
                        orgId={store.orgSelectStore.orgId ?? ""}
                        userId={store.authStore._user.profile.sub ?? ""}
                        fullHeight
                        setHierarchyIds={store.setHierarchyIds}
                        // AcxHierarchySelector only bumps hierarchiesApplied itself on exactly /app/classifiers, so on
                        // the LLM page (/app/classifiers/llm) the count would never re-check after a hierarchy Apply.
                        // The isLlm guard keeps Lucene from being bumped twice.
                        onSaveUpdateWithHierarchies={() => {
                            if (isLlm) store.setHierarchiesApplied();
                        }}
                    />
                </Grid>
                <Grid item xs={12} sx={{ width: "100%" }}>
                    {indicator}
                    {isLlm &&
                        !store.loadingAffected &&
                        store.succeeded < 0 &&
                        store.numAffectedInteractions != null && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                This is an upper bound. Conversations that do
                                not meet a model&apos;s metadata filters are
                                skipped.
                            </Typography>
                        )}
                    {isLlm &&
                        !store.loadingAffected &&
                        store.succeeded < 0 &&
                        store.numAffectedInteractions != null &&
                        store.llmReclassifyEstimateText !== undefined && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                            >
                                Estimated processing time: up to about{" "}
                                {store.llmReclassifyEstimateText}. Work only
                                runs between 5 PM and 8 AM ET, so a job
                                submitted during the day starts that evening.
                            </Typography>
                        )}
                    {isLlm &&
                        !store.loadingAffected &&
                        store.succeeded < 0 &&
                        store.numAffectedInteractions != null &&
                        costEstimate !== undefined && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                            >
                                Estimated token cost: up to about{" "}
                                {formatUsd(costEstimate.totalUsd)} (~
                                {costEstimate.totalTokens >= 1_000_000
                                    ? `${(
                                          costEstimate.totalTokens / 1_000_000
                                      ).toFixed(1)}M`
                                    : Math.round(
                                          costEstimate.totalTokens,
                                      ).toLocaleString()}{" "}
                                tokens), based on each model&apos;s average
                                usage over the last 14 days.
                                {costEstimate.unpricedModels.length > 0 &&
                                    ` Tokens for ${costEstimate.unpricedModels.join(", ")} have no known price and are not included in the cost.`}
                            </Typography>
                        )}
                    {isLlm &&
                        !store.loadingAffected &&
                        store.succeeded < 0 &&
                        store.numAffectedInteractions > 0 &&
                        store.classifiersWithoutUsage.length > 0 && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                            >
                                No token usage in the last 14 days for{" "}
                                {store.selectedClassifiers
                                    .filter((classifier) =>
                                        store.classifiersWithoutUsage.includes(
                                            classifier.id,
                                        ),
                                    )
                                    .map((classifier) => classifier.name)
                                    .join(", ")}
                                , so{" "}
                                {store.classifiersWithoutUsage.length === 1
                                    ? "it is"
                                    : "they are"}{" "}
                                not included in the cost estimate.
                            </Typography>
                        )}
                </Grid>
                {store.authStore.isUserUltra() && !isLlm && (
                    <Grid item xs={12} sx={{ width: "100%" }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={store.useV2Reclassify}
                                    onChange={store.toggleV2Reclassify}
                                />
                            }
                            label="Use V2 Reclassify"
                        />
                    </Grid>
                )}
                {reclassifyLimitExceeded && reclassifyLimitExceededError}
            </Grid>
        );

        const confirmReclassifyContent = (
            <Grid
                container
                item
                alignItems="center"
                direction="column"
                justifyContent="center"
            >
                <Grid
                    item
                    xs={12}
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        maxWidth: "95%",
                        paddingBottom: "1rem",
                    }}
                >
                    {isLlm ? (
                        <div>
                            <Typography>
                                Are you sure you want to reclassify this
                                selection? Reclassify will run all selected
                                models on every qualifying conversation in your
                                specified date range and hierarchy.
                            </Typography>
                            <Typography sx={{ marginTop: "0.75rem" }}>
                                Existing results for these models will be
                                overwritten, and each conversation uses tokens
                                {costEstimate !== undefined
                                    ? ` (estimated at up to about ${formatUsd(costEstimate.totalUsd)} in total).`
                                    : "."}
                            </Typography>
                            <Typography sx={{ marginTop: "0.75rem" }}>
                                Work is spread out over off-hours at a
                                controlled rate, so large jobs can take hours to
                                finish. A job cannot be cancelled once it has
                                started. You will receive a notification once
                                complete.
                            </Typography>
                        </div>
                    ) : (
                        <Typography>
                            Are you sure you want to reclassify this selection?
                            Reclassify will process all selected classifiers in
                            your specified date range and hierarchy. You will
                            receive a notification once complete.
                        </Typography>
                    )}
                </Grid>
            </Grid>
        );

        return (
            <>
                <AcxConfirmationDialog
                    buttonsDisabled={
                        store.reclassifySessionInProgress ||
                        reclassifyLimitExceeded ||
                        // The select is clearable; with no ids the server would read the request as the Lucene
                        // meaning and the job would fail immediately.
                        (isLlm && store.classifierIds.length === 0)
                    }
                    dialogContentStyles={{ overflow: "hidden" }}
                    isOpen={store.dialog.isOpen}
                    onClose={closeDialog}
                    onConfirm={() => {
                        store.confirmDialog.open();
                        closeDialog();
                    }}
                    content={reclassifyDialogContent}
                    confirmButtonText="Reclassify These Records"
                    isLoading={store.dialog.isLoading}
                    errorMessage={store.dialog.errorMessage}
                    title="Reclassify"
                    subTitle={
                        isLlm
                            ? "Run model(s) against selected hierarchy"
                            : "Run classifier(s) against selected hierarchy"
                    }
                    maxWidth="xl"
                    controlStyles={{ backgroundColor: "#fff" }}
                />
                <AcxConfirmationDialog
                    isOpen={store.confirmDialog.isOpen}
                    dialogContentStyles={{ textAlign: "center" }}
                    buttonsDisabled={store.reclassifySessionInProgress}
                    title="Confirm Reclassify"
                    content={confirmReclassifyContent}
                    onClose={() => {
                        closeConfirmDialog();
                        store.dialog.open();
                    }}
                    onConfirm={() => {
                        clearProgress();
                        reclassify();
                        closeConfirmDialog();
                        closeDialog();
                    }}
                    isLoading={store.confirmDialog.isLoading}
                    alternateSpinnerType="PuffLoader"
                    errorMessage={store.confirmDialog.errorMessage}
                    controlStyles={{ backgroundColor: "#fff" }}
                    centerTitle
                />
            </>
        );
    },
);

export default ClassifierBuilderV2ReclassifyDialog;
